import { useRef, useState } from "react";
import { Upload, Library, Link as LinkIcon, X, Loader2, Image as ImageIcon } from "lucide-react";
import adminApi from "@/lib/adminApi";
import MediaPicker from "./MediaPicker";

/**
 * ImageUploadField — unified 3-method image picker.
 *
 * Props:
 *   value       (string)  — current image URL
 *   onChange    (fn)      — called with new URL string
 *   label       (string)  — field label
 *   category    (string)  — media category for uploads (e.g. "product", "category")
 *   group       (string)  — media group label
 *   testIdPrefix (string) — prefix for data-testid attributes
 */
export default function ImageUploadField({
  value = "",
  onChange,
  label = "Image",
  category = "product",
  group = "Products",
  testIdPrefix = "image-field",
}) {
  const [uploading, setUploading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef(null);

  const doUpload = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      setUploadError("Please select an image file.");
      return;
    }
    setUploadError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      fd.append("category", category);
      fd.append("group", group);
      fd.append("status", "published");
      const res = await adminApi.post("/api/admin/media/upload", fd);
      onChange(res.data.file_url || "");
    } catch (err) {
      console.error("[ImageUploadField] upload failed:", err);
      setUploadError("Upload failed. Please try again or paste a URL.");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFileChange = (e) => doUpload(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    doUpload(e.dataTransfer.files?.[0]);
  };

  const handlePickerSelect = (url) => {
    onChange(url);
    setShowPicker(false);
  };

  return (
    <div className="block md:col-span-2" data-testid={testIdPrefix}>
      <span className="text-[10px] text-white/50 uppercase tracking-wider block mb-2">{label}</span>

      {value ? (
        /* ── Preview state ── */
        <div className="relative mb-3 w-full max-w-[240px]">
          <div className="aspect-video rounded overflow-hidden border border-white/10 bg-black">
            <img
              src={value}
              alt="Preview"
              className="w-full h-full object-cover"
              data-testid={`${testIdPrefix}-preview`}
            />
          </div>
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-black border border-white/15 rounded-full text-white/50 hover:text-white hover:border-white/40 transition-colors"
            data-testid={`${testIdPrefix}-remove`}
            title="Remove image"
          >
            <X size={10} />
          </button>
          {/* Replace buttons below preview */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1 text-[10px] text-[#CBA052] border border-[#CBA052]/30 px-2 py-1 rounded hover:bg-[#CBA052]/10 disabled:opacity-50 transition-colors"
              data-testid={`${testIdPrefix}-replace-upload`}
            >
              {uploading ? <Loader2 size={9} className="animate-spin" /> : <Upload size={9} />}
              Replace
            </button>
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className="flex items-center gap-1 text-[10px] text-white/40 border border-white/10 px-2 py-1 rounded hover:text-white/70 hover:border-white/20 transition-colors"
              data-testid={`${testIdPrefix}-replace-library`}
            >
              <Library size={9} /> Library
            </button>
            <button
              type="button"
              onClick={() => setShowUrlInput(true)}
              className="flex items-center gap-1 text-[10px] text-white/40 border border-white/10 px-2 py-1 rounded hover:text-white/70 hover:border-white/20 transition-colors"
              data-testid={`${testIdPrefix}-replace-url`}
            >
              <LinkIcon size={9} /> URL
            </button>
          </div>
        </div>
      ) : (
        /* ── Empty state: drop zone ── */
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className={`relative mb-3 w-full cursor-pointer rounded border-2 border-dashed transition-colors flex flex-col items-center justify-center gap-2 py-8 ${dragOver ? "border-[#CBA052]/60 bg-[#CBA052]/5" : "border-white/10 bg-white/[0.01] hover:border-white/20 hover:bg-white/[0.02]"}`}
          data-testid={`${testIdPrefix}-dropzone`}
        >
          {uploading ? (
            <>
              <Loader2 size={22} className="animate-spin text-[#CBA052]" />
              <p className="text-xs text-white/50">Uploading...</p>
            </>
          ) : (
            <>
              <ImageIcon size={22} className="text-white/15" />
              <p className="text-xs font-medium text-white/40">
                Drop image here or <span className="text-[#CBA052]">click to browse</span>
              </p>
              <p className="text-[10px] text-white/20">PNG, JPG, WEBP — max 10 MB</p>
            </>
          )}
        </div>
      )}

      {/* Error message */}
      {uploadError && (
        <p className="text-[10px] text-red-400 mb-2" data-testid={`${testIdPrefix}-error`}>{uploadError}</p>
      )}

      {/* Action buttons row (empty state) */}
      {!value && (
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 text-xs font-medium text-[#CBA052] border border-[#CBA052]/30 px-3 py-2 rounded hover:bg-[#CBA052]/10 disabled:opacity-50 transition-colors"
            data-testid={`${testIdPrefix}-upload-btn`}
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? "Uploading..." : "Upload from Computer"}
          </button>
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="flex items-center gap-1.5 text-xs text-white/40 border border-white/10 px-3 py-2 rounded hover:text-white/70 hover:border-white/20 transition-colors"
            data-testid={`${testIdPrefix}-library-btn`}
          >
            <Library size={12} /> Choose from Library
          </button>
          <button
            type="button"
            onClick={() => setShowUrlInput((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-white/40 border border-white/10 px-3 py-2 rounded hover:text-white/70 hover:border-white/20 transition-colors"
            data-testid={`${testIdPrefix}-url-toggle`}
          >
            <LinkIcon size={12} /> Paste URL
          </button>
        </div>
      )}

      {/* URL paste input — shown when toggled or replacing */}
      {(showUrlInput || (value && showUrlInput)) && (
        <div className="flex items-center gap-2 mt-1">
          <input
            type="url"
            defaultValue={value}
            onBlur={(e) => { onChange(e.target.value.trim()); setShowUrlInput(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") { onChange(e.target.value.trim()); setShowUrlInput(false); } if (e.key === "Escape") setShowUrlInput(false); }}
            autoFocus
            placeholder="https://example.com/image.jpg"
            className="flex-1 bg-white/[0.04] border border-white/10 focus:border-[#CBA052]/40 text-white text-xs rounded px-3 py-2 outline-none"
            data-testid={`${testIdPrefix}-url-input`}
          />
          <button type="button" onClick={() => setShowUrlInput(false)} className="text-white/30 hover:text-white"><X size={13} /></button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />

      {/* Media Picker Modal */}
      {showPicker && (
        <MediaPicker
          slotLabel={label}
          onSelect={handlePickerSelect}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
